import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Settings2, FolderOpen, Folder, CheckCircle2, AlertCircle } from "lucide-react";
import { pickLedgerDir, getLedgerDir } from "../services/inventory";

export default function Settings() {
  const navigate = useNavigate();
  const [dirName, setDirName] = useState<string | null>(null);
  const [state, setState] = useState<"idle" | "picking" | "done" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    getLedgerDir().then((h) => setDirName(h?.name ?? null));
  }, []);

  const handlePick = async () => {
    setState("picking");
    setErrorMsg("");
    try {
      const handle = await pickLedgerDir();
      setDirName(handle.name);
      setState("done");
      setTimeout(() => setState("idle"), 2000);
    } catch (e: any) {
      if (e?.name !== "AbortError" && e?.name !== "SecurityError") {
        setState("error");
        setErrorMsg(e?.message || "Failed to pick folder");
      } else {
        setState("idle");
      }
    }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-citilux-blue-600/20 flex items-center justify-center">
          <Settings2 className="w-5 h-5 text-citilux-blue-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Settings</h1>
          <p className="text-sm text-gray-500 dark:text-metallic-silver/60">Ledger folder &amp; storage</p>
        </div>
      </div>

      <div className="bg-white dark:bg-navy-800/40 border border-light-border dark:border-navy-700/40 rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <Folder className="w-5 h-5 text-citilux-blue-400" />
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Ledger Directory</p>
            <p className="text-xs text-gray-400 dark:text-metallic-silver/50">
              {dirName ? (
                <>
                  Currently: <code className="text-citilux-blue-400 bg-gray-100 dark:bg-navy-700 px-1 rounded">{dirName}</code>
                </>
              ) : (
                "Not connected"
              )}
            </p>
          </div>
        </div>

        {state === "done" && (
          <div className="flex items-center gap-2 text-green-500 text-sm">
            <CheckCircle2 className="w-4 h-4" />
            Folder updated
          </div>
        )}

        {state === "error" && (
          <div className="flex items-center gap-2 text-red-500 text-sm">
            <AlertCircle className="w-4 h-4" />
            {errorMsg}
          </div>
        )}

        <button
          onClick={handlePick}
          disabled={state === "picking"}
          className="flex items-center gap-2 bg-citilux-blue-600 hover:bg-citilux-blue-500 disabled:bg-gray-400 dark:disabled:bg-navy-700 text-sm font-semibold text-white rounded-xl px-4 py-2 transition-colors"
        >
          {state === "picking" ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <FolderOpen className="w-4 h-4" />
          )}
          {dirName ? "Change Folder" : "Select Folder"}
        </button>
      </div>
    </div>
  );
}
